import React from "react";
import { useState } from "react";
import { formatLettresDate } from "../../utils/ClasseEvaluations";
import { useMemo } from "react";
import { renderTextWithLineBreaks } from "../../utils/DeleteBr";
import { Calendar, User, Users, ChevronDown } from 'lucide-react';

// ─── Palette cohérente avec AcquisitionEleve ───
const P = {
  textPrimary: "#1A2744",
  textSecondary: "#6B7A99",
  textMuted: "#9AA5BD",
  borderLight: "rgba(30,58,138,0.08)",
  green800: "#166534", green500: "#22C55E", teal400: "#2DD4BF",
  yellow500: "#EAB308", orange500: "#F97316", red500: "#EF4444",
  gray400: "#9CA3AF",
};

const SCORE_MAP = {
  6: { label: "Très bonne maîtrise", color: P.green800, bg: "rgba(22,101,52,0.08)", border: "rgba(22,101,52,0.15)", dot: "bg-green-800" },
  5: { label: "Maîtrise satisfaisante", color: P.green500, bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.15)", dot: "bg-green-500" },
  4: { label: "Presque maîtrisé", color: P.teal400, bg: "rgba(45,212,191,0.08)", border: "rgba(45,212,191,0.18)", dot: "bg-teal-400" },
  3: { label: "Maîtrise fragile", color: P.yellow500, bg: "rgba(234,179,8,0.08)", border: "rgba(234,179,8,0.15)", dot: "bg-yellow-500" },
  2: { label: "Début de maîtrise", color: P.orange500, bg: "rgba(249,115,22,0.08)", border: "rgba(249,115,22,0.15)", dot: "bg-orange-500" },
  1: { label: "Maîtrise insuffisante", color: P.red500, bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.15)", dot: "bg-red-500" },
  0: { label: "Non évalué", color: P.gray400, bg: "rgba(156,163,175,0.06)", border: "rgba(156,163,175,0.12)", dot: "bg-gray-400" },
};

const getScoreConf = (s) => SCORE_MAP[s] || SCORE_MAP[0];

// ─── Grain texture ───
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='150' height='150' filter='url(%23n)' opacity='.5'/%3E%3C/svg%3E\")";

// ─── CSS responsive ───
const PASSIF_CSS = `
  .passif-root {
    --p-pad: 20px; --p-card-pad: 22px 24px; --p-card-r: 18px;
    --p-title: 16px; --p-meta: 13px; --p-score: 24px;
    --p-savoir-pad: 14px 18px; --p-comp-pad: 12px 16px;
    --p-label: 12px; --p-gap: 16px;
  }
  @media (max-width:639px) {
    .passif-root { --p-pad: 12px; --p-card-pad: 16px 18px; --p-card-r: 14px; --p-title: 14px; --p-meta: 11px; --p-score: 20px; --p-gap: 12px; }
  }
  @media (min-width:1280px) and (max-width:1699px) {
    .passif-root { --p-pad: 12px; --p-card-pad: 16px 18px; --p-card-r: 14px; --p-title: 13px; --p-meta: 11px; --p-score: 20px; --p-savoir-pad: 10px 14px; --p-comp-pad: 8px 12px; --p-label: 10px; --p-gap: 10px; }
  }
  @media (min-width:1700px) and (max-width:1919px) {
    .passif-root { --p-pad: 16px; --p-card-pad: 18px 20px; --p-card-r: 16px; --p-title: 14px; --p-meta: 12px; --p-score: 22px; --p-savoir-pad: 12px 16px; --p-comp-pad: 10px 14px; --p-label: 11px; --p-gap: 12px; }
  }

  .passif-card {
    transition: all 0.35s cubic-bezier(.4,0,.2,1);
  }
  .passif-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 56px rgba(30,58,138,0.12), 0 8px 20px rgba(30,58,138,0.07), 0 2px 6px rgba(30,58,138,0.04), inset 0 1px 0 rgba(255,255,255,0.95) !important;
  }

  .savoir-row { transition: all 0.2s ease; }
  .savoir-row:hover { background: rgba(30,58,138,0.03) !important; }

  .comp-expand { animation: compSlide 0.25s ease-out; }
  @keyframes compSlide { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
`;

const PassifEleve = ({eleve}) => {
  const [selectedEval, setSelectedEval] = useState(null);
  const [expandedEvals, setExpandedEvals] = useState(new Set());

  const groupedData = useMemo(() => {
    const data = eleve.resultats.reduce((acc, item) => {
      const { evalId, evalTitre, evalDate, groupNom, classeNom, savoirFaireId,
         profCreateurNom, profCreateurPrenom, savoirTitre, attenduId, attenduTitre, competenceId,
          competenceNom, scores, themeNom } = item;
      if (!acc[evalId]) {
        acc[evalId] = {
          evalId,
          evalTitre,
          profCreateurNom,
          profCreateurPrenom,
          evalDate,
          groupNom,
          classeNom,
          savoirsFaire: {},
          scoreTotal:0,
          count:0,
        };
      }
      const id = savoirFaireId || attenduId;
      const titre = savoirTitre || attenduTitre;
      const type = savoirFaireId ? 'savoir' : 'attendu';
      const uniqueKey = `${type}-${id}`;

      if (!acc[evalId].savoirsFaire[uniqueKey]) {
        acc[evalId].savoirsFaire[uniqueKey] = {
          titre,
          themeNom,
          competences: [],
        };
      }

      acc[evalId].savoirsFaire[uniqueKey].competences.push({
        competenceId,
        competenceNom,
        scores,
      });

      if(scores) {
        acc[evalId].scoreTotal += scores || 0;
        acc[evalId].count += 1;
      }

      return acc;
    }, {});

    Object.keys(data).forEach(evalId => {
      if (data[evalId].count > 0) {
        data[evalId].moyenne = (((data[evalId].scoreTotal / data[evalId].count) * 20)/6).toFixed(2);
      } else {
        data[evalId].moyenne = "0.00";
      }
    });

    return data;
  }, [eleve]);

  const handleEvalClick = (item) => {
    if(selectedEval) {
      setSelectedEval(null);
    } else {
      setSelectedEval(item);
    }
  };

  const toggleEvalExpansion = (evalId) => {
    const newExpanded = new Set(expandedEvals);
    if (newExpanded.has(evalId)) {
      newExpanded.delete(evalId);
    } else {
      newExpanded.add(evalId);
    }
    setExpandedEvals(newExpanded);
  };

  const getMoyenneColor = (m) => {
    const n = parseFloat(m);
    if (n >= 16) return P.green800;
    if (n >= 12) return P.green500;
    if (n >= 8) return P.yellow500;
    if (n >= 4) return P.orange500;
    if (n > 0) return P.red500;
    return P.gray400;
  };

  return (
    <div className="passif-root" style={{ padding: "var(--p-pad)", minHeight: 200 }}>
      <style>{PASSIF_CSS}</style>

      <div style={{ width: "100%" }}>
        {!selectedEval &&
          <>
            {groupedData && Object.values(groupedData).map((s, index) => (
              <div
                key={s.evalId}
                className="passif-card"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.86) 70%, rgba(235,243,255,0.78) 100%)",
                  backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                  padding: "var(--p-card-pad)", borderRadius: "var(--p-card-r)",
                  border: "1px solid rgba(255,255,255,0.65)",
                  boxShadow: "0 12px 40px rgba(30,58,138,0.1), 0 4px 12px rgba(30,58,138,0.06), 0 1px 3px rgba(30,58,138,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
                  marginBottom: "var(--p-gap)", position: "relative", overflow: "hidden",
                  opacity: 0, animation: `compSlide 0.4s ease-out ${index * 0.06}s forwards`,
                }}
              >
                {/* Grain */}
                <div style={{
                  position: "absolute", inset: 0, opacity: 0.018, pointerEvents: "none",
                  borderRadius: "var(--p-card-r)", overflow: "hidden",
                  backgroundImage: GRAIN, mixBlendMode: "multiply",
                  background: "linear-gradient(135deg, rgba(30,58,138,0.03), rgba(59,95,204,0.02))"
                }} />

                <div style={{ position: "relative", zIndex: 1 }}>
                  {/* En-tête de l'évaluation */}
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", paddingBottom: 14, marginBottom: 14, borderBottom: `1.5px solid ${P.borderLight}`, gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ margin: 0, fontSize: "var(--p-title)", fontWeight: 700, lineHeight: 1.35, color: P.textPrimary }}>
                        {s.evalTitre}
                      </h3>
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginTop: 8 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "var(--p-meta)", color: P.textMuted }}>
                          <Calendar size={14} />
                          {new Date(s.evalDate).toLocaleDateString('fr-FR')}
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "var(--p-meta)", color: P.textMuted }}>
                          <Users size={14} />
                          {s.classeNom || s.groupNom}
                        </span>
                        {s.profCreateurNom && (
                          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "var(--p-meta)", color: P.textMuted }}>
                            <User size={14} />
                            {s.profCreateurNom + " " + s.profCreateurPrenom}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Note globale */}
                    <div style={{
                      textAlign: "center", padding: "8px 16px", borderRadius: 12,
                      background: "rgba(30,58,138,0.04)", border: `1px solid ${P.borderLight}`,
                      minWidth: 80, flexShrink: 0,
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: P.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                        Note
                      </div>
                      {s.moyenne > 0 ? (
                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 2 }}>
                          <span style={{ fontSize: "var(--p-score)", fontWeight: 800, fontFamily: "monospace", color: getMoyenneColor(s.moyenne), lineHeight: 1 }}>
                            {s.moyenne}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 500, color: P.textMuted }}>/20</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 600, color: P.textMuted }}>N/E</span>
                      )}
                    </div>
                  </div>

                  {/* Liste des savoir-faire évalués */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: "var(--p-label)", fontWeight: 700, color: P.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>
                      Savoirs évalués
                    </div>

                    {Object.values(s.savoirsFaire).map((savoir, idx) => {
                      const savoirId = `eval-${s.evalId}-savoir-${savoir.titre}`;
                      const isExpanded = expandedEvals.has(savoirId);

                      const competencesSummary = savoir.competences.reduce((acc, comp) => {
                        const score = comp.scores;
                        if(score === 6) acc.vert_plus = (acc.vert_plus || 0) + 1;
                        else if (score === 5) acc.vert = (acc.vert || 0) + 1;
                        else if (score === 4) acc.vert_pm = (acc.vert_pm || 0) + 1;
                        else if (score === 3) acc.yellow = (acc.yellow || 0) + 1;
                        else if (score === 2) acc.orange = (acc.orange || 0) + 1;
                        else if (score === 1) acc.red = (acc.red || 0) + 1;
                        else acc.none = (acc.none || 0) + 1;
                        return acc;
                      }, {});

                      const dots = [
                        { key: "vert_plus", color: P.green800 },
                        { key: "vert", color: P.green500 },
                        { key: "vert_pm", color: P.teal400 },
                        { key: "yellow", color: P.yellow500 },
                        { key: "orange", color: P.orange500 },
                        { key: "red", color: P.red500 },
                        { key: "none", color: P.gray400 },
                      ];

                      return (
                        <div key={savoirId} style={{
                          borderRadius: 12, overflow: "hidden",
                          border: `1px solid ${isExpanded ? "rgba(30,58,138,0.12)" : P.borderLight}`,
                          transition: "border-color 0.2s ease",
                        }}>
                          {/* En-tête du savoir-faire */}
                          <div
                            className="savoir-row"
                            onClick={() => toggleEvalExpansion(savoirId)}
                            style={{
                              padding: "var(--p-savoir-pad)", cursor: "pointer",
                              background: isExpanded ? "rgba(30,58,138,0.03)" : "rgba(30,58,138,0.015)",
                              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: "var(--p-meta)", fontWeight: 600, color: P.textPrimary, marginBottom: 6 }}>
                                {savoir.titre}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                {/* Dots résumé */}
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  {dots.map(d => competencesSummary[d.key] > 0 && (
                                    <span key={d.key} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: d.color, boxShadow: `0 0 4px ${d.color}33` }} />
                                      <span style={{ fontSize: 10, fontWeight: 600, color: P.textSecondary }}>{competencesSummary[d.key]}</span>
                                    </span>
                                  ))}
                                </div>
                                <span style={{ fontSize: 10, color: P.textMuted }}>
                                  {savoir.competences.length} compétence{savoir.competences.length > 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                            <ChevronDown
                              size={16}
                              style={{
                                color: P.textMuted, flexShrink: 0,
                                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.25s ease",
                              }}
                            />
                          </div>

                          {/* Détail des compétences */}
                          {isExpanded && (
                            <div className="comp-expand" style={{ padding: "var(--p-comp-pad)", borderTop: `1px solid ${P.borderLight}` }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {savoir.competences.map((comp, compIdx) => {
                                  const sc = getScoreConf(comp.scores);
                                  return (
                                    <div
                                      key={compIdx}
                                      style={{
                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                        padding: "8px 12px", borderRadius: 10, gap: 10,
                                        background: "rgba(30,58,138,0.015)",
                                        transition: "background 0.15s ease",
                                      }}
                                    >
                                      <span style={{ fontSize: "var(--p-label)", color: P.textSecondary, flex: 1, minWidth: 0 }}>
                                        {comp.competenceNom}
                                      </span>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                        <span style={{
                                          fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 8,
                                          background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                                          whiteSpace: "nowrap",
                                        }}>
                                          {sc.label}
                                        </span>
                                        <div style={{
                                          width: 14, height: 14, borderRadius: "50%",
                                          background: sc.color, boxShadow: `0 0 6px ${sc.color}33`,
                                        }} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </>
        }
      </div>
    </div>
  );
};

export default PassifEleve;
